//
// Copyright (c) 2023 Emplifi, Inc.
//

// @ts-ignore
import i18next from "i18n/i18n";
// @ts-ignore
import $ from "jquery";
// @ts-ignore
import "jquery-ui/ui/widgets/accordion";
import {
  addListToDropdown,
  Collection,
  createDropdown,
  createElement,
  Dialog,
  ElementReplacer,
  EventInfo,
  ListDropdownButtonDefinition,
  ListDropdownItemDefinition,
  Plugin,
  SplitButtonView,
  View,
  ViewDowncastWriter,
  ViewElement,
  ViewModel,
} from "ckeditor5";
import PreviewCommand from "./previewCommand";
import { removeCkReset } from "../ckEditorUtil";
// @ts-ignore
import KbMenuModel from "modules/header/kbMenu/kbMenuModel";
// @ts-ignore
import SnippetModel from "modules/topicsSplit/snippet/snippetModel";
// @ts-ignore
import TopicResponseModel from "modules/topicsSplit/topic/topicResponse/topicResponseModel";

// @ts-ignore
import previewIcon from "img/svg/preview.svg";

const COMMAND_FORCE_DISABLE_ID = "PreviewMode";

export default class PreviewUi extends Plugin {
  private command: PreviewCommand | undefined;
  private elementReplacer = new ElementReplacer();
  private replacedRootElement: ViewElement | null | undefined;

  // noinspection JSUnusedGlobalSymbols
  public static get pluginName() {
    return "PreviewUi";
  }

  public init() {
    this.command = this.editor.commands.get("preview");
    this._addToolbarButton();
    this._setupCommandListener();
  }

  private _addToolbarButton() {
    this.editor.ui.componentFactory.add("preview", (locale) => {
      const editor = this.editor;
      const command = this.command;

      const dropdownView = createDropdown(locale, SplitButtonView);
      dropdownView.panelPosition = locale.uiLanguageDirection === "rtl" ? "se" : "sw";
      const splitButton = dropdownView.buttonView as SplitButtonView;

      splitButton.set({
        tooltip: true,
        label: i18next.t("ckeditor.preview.buttonName"),
      });
      splitButton.actionView.set({
        icon: previewIcon,
      });

      if (command) {
        dropdownView.bind("isEnabled").to(command);
        splitButton.bind("isOn").to(command, "value", (value) => !!value);
      }

      this.listenTo(splitButton, "execute", () => {
        editor.execute("preview");
      });

      const agentAssistMenuItem: ListDropdownButtonDefinition = {
        type: "button",
        model: new ViewModel({
          label: i18next.t("ckeditor.preview.agentAssistMenuItem"),
          withText: true,
          agentAssistPreview: true,
        }),
      };

      const itemDefinitions = new Collection<ListDropdownItemDefinition>();
      itemDefinitions.add(agentAssistMenuItem);

      addListToDropdown(dropdownView, itemDefinitions, {
        role: "menu",
        ariaLabel: i18next.t("ckeditor.preview.agentAssistMenuAriaLabel"),
      });

      type PreviewSource = {
        agentAssistPreview?: boolean;
      };

      this.listenTo(dropdownView, "execute", (evt: EventInfo) => {
        const source = evt.source as PreviewSource;

        if (source?.agentAssistPreview) {
          this._ensureInlinePreviewOff();
          this._openAgentAssistPreviewOverlay();
        }
      });

      return dropdownView;
    });
  }

  private _ensureInlinePreviewOff() {
    if (this.command?.value) {
      this.editor.execute("preview");
    }
  }

  private _openAgentAssistPreviewOverlay() {
    const editor = this.editor;
    const locale = editor.locale;

    const dialog: Dialog = editor.plugins.get(Dialog);

    const contentHtml = editor.data.get();
    const response = TopicResponseModel.responseTemplate.prepareResponseForSave(contentHtml);

    const previewView = new View(locale);

    previewView.setTemplate({
      tag: "div",
      attributes: {
        class: ["ak-agent-assist-preview-body"],
      },
      children: [
        {
          tag: "div",
          attributes: {
            class: ["ak-agent-assist-preview-content"],
          },
        },
      ],
    });

    previewView.on("render", () => {
      const container = previewView.element!.querySelector(
        ".ak-agent-assist-preview-content",
      ) as HTMLElement;

      container.innerHTML = response;

      // Must happen after dialog attaches CK reset classes
      setTimeout(() => {
        removeCkReset(previewView.element!);
        this._initializeAccordions(container);
      }, 100);
    });

    dialog.show({
      id: "ak-agent-assist-preview-dialog",
      className: "ak-agent-assist-preview-dialog",
      title: i18next.t("ckeditor.preview.agentAssistDialogTitle"),
      content: previewView,
      isModal: true,
    });
  }

  private _initializeAccordions(container: HTMLElement) {
    ($(container).find(".accordion") as any).accordion({
      collapsible: true,
      heightStyle: "content",
    });
  }

  private _createPreviewElement(response: string, ownerDocument: Document): HTMLElement {
    const previewElement = createElement(ownerDocument, "div", {
      class: "ck ck-rounded-corners ak-preview-area",
    });
    const config = this.editor.config;
    const allCategories = config.get("allCategories");
    const allSnippets: SnippetModel.snippets = config.get("allSnippets");
    const attributes = config.get("attributes");
    const categories = config.get("categories");
    const languageId = config.get("languageId");
    const responseId = config.get("responseId");
    let previewResponse = KbMenuModel.categories.translateCategoryForResponse(
      response,
      responseId,
      languageId,
      categories,
      allCategories,
      false,
    );
    previewResponse = KbMenuModel.attributes.translateAttributesForResponse(
      previewResponse,
      responseId,
      languageId,
      attributes,
      false,
    );
    previewResponse = allSnippets.translateSnippetsForResponse({
      responseString: previewResponse,
    });
    previewElement.insertAdjacentHTML("afterbegin", previewResponse);
    return previewElement;
  }

  private _disableCommands() {
    const editor = this.editor;
    for (const command of editor.commands.commands()) {
      if (command != this.command) {
        command.forceDisabled(COMMAND_FORCE_DISABLE_ID);
      }
    }
    if (editor.plugins.has("SourceEditing")) {
      // The Source editing ToolBar button is not tied to a Command, so need to disable
      // the plugin directly
      const sourceEditing = editor.plugins.get("SourceEditing") as Plugin;
      if (sourceEditing) {
        sourceEditing.isEnabled = false;
      }
    }
  }

  private _enableCommands() {
    const editor = this.editor;
    for (const command of editor.commands.commands()) {
      command.clearForceDisabled(COMMAND_FORCE_DISABLE_ID);
    }
    if (editor.plugins.has("SourceEditing")) {
      // The Source editing ToolBar button is not tied to a Command, so need to enable
      // the plugin directly
      const sourceEditing = editor.plugins.get("SourceEditing") as Plugin;
      if (sourceEditing) {
        sourceEditing.isEnabled = true;
      }
    }
  }

  private _hidePreview() {
    // This was adapted from code for the Source Editing feature
    const editor = this.editor;
    const editingView = editor.editing.view;
    // Show the original editor again
    this.elementReplacer.restore();
    editingView.change((writer: ViewDowncastWriter) => {
      // @ts-ignore
      writer.removeClass("ck-hidden", this.replacedRootElement);
    });
    editingView.focus();
    this.replacedRootElement = undefined;
  }

  private _setupCommandListener() {
    if (this.command) {
      this.command.on("change:value", (_event, _name: string, value) => {
        if (value) {
          this._disableCommands();
          this._showPreview();
        } else {
          this._hidePreview();
          this._enableCommands();
        }
      });
    }
  }

  private _showPreview() {
    // This was adapted from code for the Source Editing feature
    const editor = this.editor;
    const editingView = editor.editing.view;
    const model = editor.model;
    // Clear the current selection to avoid issues while replacing the editor area
    model.change((writer) => {
      writer.setSelection(null);
      writer.removeSelectionAttribute(model.document.selection.getAttributeKeys());
    });
    // Currently only support the Classic Editor, which only includes a single root
    const [rootName, rootElement] = Array.from(editingView.domRoots.entries())[0];
    const previewElement = this._createPreviewElement(editor.data.get(), rootElement.ownerDocument);
    editingView.change((writer) => {
      this.replacedRootElement = editingView.document.getRoot(rootName);
      // @ts-ignore
      writer.addClass("ck-hidden", this.replacedRootElement);
    });
    this.elementReplacer.replace(rootElement, previewElement);
    // Register the element, so it becomes available for Alt+F10 and Esc navigation.
    editor.ui.setEditableElement("preview:" + rootName, previewElement);
  }
}
